import { useState } from "react";
import { useParams } from "react-router-dom";
import { Cpu, Loader2, X } from "lucide-react";
import { fdate, imgUrl, label, useApi } from "@/lib/api";
import { asMedia } from "@/components/Media";

export default function Gallery() {
  const { token } = useParams();
  const { data, loading } = useApi(`/public/gallery/${token}`);
  const [zoom, setZoom] = useState(null);
  if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!data) return <div className="h-screen flex items-center justify-center text-sm text-muted-foreground">This gallery link is invalid.</div>;
  const media = data.stages.flatMap((s) => s.photos.map((m) => ({ ...asMedia(m), stage: s.stageName })));
  const all = media.filter((m) => m.kind === "image").map((m) => ({ p: m.path, stage: m.stage }));
  const extra = media.filter((m) => m.kind !== "image");
  const [hero, ...rest] = all;
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="build-gallery">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-md bg-white text-black flex items-center justify-center"><Cpu className="h-4 w-4" /></div>
        <span className="font-display font-bold">Gamerz Nation</span>
        <span className="ml-auto text-xs text-zinc-400 font-mono">{data.projectCode}</span>
      </header>
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-10 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Build showcase · {fdate(data.deliveredAt)}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-3" data-testid="gallery-title">{data.customerFirstName}'s {data.workflowName}</h1>
        <p className="text-zinc-400 mt-3 max-w-xl text-sm sm:text-base">Hand-assembled, stress-tested and photographed in our workshop before delivery.</p>
      </section>
      <section className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-4" data-testid="gallery-photos">
        {hero && (
          <button onClick={() => setZoom(hero)} className="lg:col-span-2 lg:row-span-2 relative overflow-hidden rounded-xl group" data-testid="gallery-photo-0">
            <img src={imgUrl(hero.p)} alt={hero.stage} className="h-full w-full object-cover aspect-[4/3] group-hover:scale-[1.02] transition-transform duration-500" />
            <span className="absolute left-3 bottom-3 text-[11px] rounded bg-black/60 px-2 py-1">{hero.stage}</span>
          </button>
        )}
        {rest.map((x, i) => (
          <button key={x.p + i} onClick={() => setZoom(x)} className="relative overflow-hidden rounded-xl group" data-testid={`gallery-photo-${i + 1}`}>
            <img src={imgUrl(x.p)} alt={x.stage} className="h-full w-full object-cover aspect-[4/3] group-hover:scale-[1.03] transition-transform duration-500" />
            <span className="absolute left-3 bottom-3 text-[11px] rounded bg-black/60 px-2 py-1">{x.stage}</span>
          </button>
        ))}
        {!all.length && <div className="lg:col-span-3 rounded-xl border border-zinc-800 py-20 text-center text-sm text-zinc-500">Photos coming soon.</div>}
      </section>
      {extra.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pt-10 grid sm:grid-cols-2 gap-4" data-testid="gallery-videos">
          {extra.map((m, i) => m.kind === "video"
            ? <video key={i} src={imgUrl(m.path)} controls className="w-full rounded-xl bg-black aspect-video" />
            : <a key={i} href={m.path} target="_blank" rel="noreferrer" className="rounded-xl border border-zinc-800 p-5 text-sm hover:border-zinc-500 break-all">Watch: {m.path}</a>)}
        </section>
      )}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-400 mb-6">Specifications</h2>
        <div className="grid sm:grid-cols-2 gap-x-10" data-testid="gallery-specs">
          {data.specs.map((s, i) => (
            <div key={i} className="flex gap-4 border-b border-zinc-800 py-3 text-sm">
              <span className="w-32 shrink-0 text-zinc-500">{label(s.category)}</span><span>{s.name}</span>
            </div>
          ))}
        </div>
      </section>
      <footer className="max-w-6xl mx-auto px-6 pb-10 text-xs text-zinc-500">© Gamerz Nation · Custom PCs, engineered to perform</footer>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setZoom(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-zinc-300" data-testid="gallery-lightbox-close"><X className="h-6 w-6" /></button>
          <img src={imgUrl(zoom.p)} alt={zoom.stage} className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
